import * as React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { CheckOutIdentity, DocumentCheckOutAdapter, useHostAdapter } from "../../../platform";
import { describeReadFailure } from "../../../platform/shared/hostReadErrors";
import { readCheckOutIdentityOnce } from "../../../platform/shared/identityOnce";
import { DocumentCheckOutState, useGetCheckOutStatesQuery } from "../api/checkoutApi";

// On open, the identity part is verified against the API: a stale part means the
// document was checked in elsewhere, discarded, or force released — say which state
// we're in and disable the actions, never fail obscurely mid-save.

/** Automatic retries of the identity read, and the wait between them. */
const MAX_READ_RETRIES = 3;
const READ_RETRY_MS = 1500;

export type VerificationStatus =
  | "loading"
  /** No <jyloCheckOut> part: this document is not checked out from Jylo. */
  | "no-identity"
  /** Part present but the draft is no longer open (checked in / discarded / force released). */
  | "stale"
  /** The draft is open but held by someone else now (force + re-check-out elsewhere). */
  | "held-by-other"
  /** The draft is chat-scoped: manual editing is blocked. */
  | "chat-scoped"
  /**
   * The host refused to hand over its custom XML parts, so we do not KNOW whether this
   * document is checked out. Distinct from "no-identity" on purpose: saying "not
   * checked out from Jylo" about a document that is sends people hunting for the wrong
   * bug. Retryable, and in practice a retry works.
   */
  | "read-failed"
  | "ok";

export interface CheckOutVerification {
  status: VerificationStatus;
  identity: CheckOutIdentity | null;
  state: DocumentCheckOutState | null;
  /**
   * Set only when status is "read-failed". Carried separately from the generic status
   * message because the useful thing to say depends on WHY the host refused -- a cell
   * left in edit mode has a specific, actionable fix.
   */
  readFailure?: string;
}

export const useCheckOutVerification = (
  documents: DocumentCheckOutAdapter
): CheckOutVerification & { refresh: () => void } => {
  const { user } = useAuth0();
  const host = useHostAdapter();
  const [identity, setIdentity] = React.useState<CheckOutIdentity | null>(null);
  const [identityChecked, setIdentityChecked] = React.useState(false);
  const [readFailure, setReadFailure] = React.useState<string | null>(null);
  // Bumped to re-run the read. The host can refuse transiently -- it is busy, or the
  // workbook is in a state that blocks the API -- and asking again is usually enough.
  const [readAttempt, setReadAttempt] = React.useState(0);
  // Budget for the automatic retries below, spent per mount and refilled by refresh().
  const retriesLeft = React.useRef(MAX_READ_RETRIES);

  React.useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    setIdentityChecked(false);
    setReadFailure(null);

    // Through the cache, not straight at the host: warmed at pane mount, so a ribbon
    // press fired out of a cell edit reads the answer rather than asking Excel for it
    // at the one moment Excel will not say.
    readCheckOutIdentityOnce(documents)
      .then((result) => {
        if (cancelled) return;
        setIdentity(result);
        setReadFailure(null);
      })
      .catch((error: unknown) => {
        // A rejection means we could not look, NOT that there is nothing to find. The
        // catch also has to exist at all: without it the promise rejects unhandled and
        // identityChecked never flips, leaving the pane on its spinner for ever.
        if (cancelled) return;
        setIdentity(null);
        setReadFailure(describeReadFailure(error, host.displayName));

        // Ask again, a few times, before leaving the failure on screen.
        //
        // The refusal this is here for is transient by nature -- the host is busy, or
        // a cell is part-way through an edit -- and the pane has no way to know when it
        // has passed. Without this the first answer was the last one: the failure sat
        // there until something called refresh(), and the only control that does is
        // behind a button this same failure disables.
        if (retriesLeft.current > 0) {
          retriesLeft.current -= 1;
          retryTimer = setTimeout(() => setReadAttempt((attempt) => attempt + 1), READ_RETRY_MS);
        }
      })
      .finally(() => {
        if (!cancelled) setIdentityChecked(true);
      });

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [documents, host.displayName, readAttempt]);

  const {
    data: states,
    isLoading,
    refetch,
  } = useGetCheckOutStatesQuery(
    { projectObjectIdentifier: identity?.projectObjectIdentifier ?? "" },
    { skip: !identity, refetchOnMountOrArgChange: true }
  );

  const refresh = () => {
    // A press is a fresh budget: the user has done something -- clicked into the pane,
    // finished their edit -- that the automatic attempts could not do for them.
    retriesLeft.current = MAX_READ_RETRIES;
    setReadAttempt((attempt) => attempt + 1);
    // Only once there is an identity to ask about: the states query is skipped until then,
    // and RTK throws on a refetch of a query it never started. A newly read identity
    // changes the query's argument, which fetches by itself.
    if (identity) refetch();
  };

  if (!identityChecked || (identity && isLoading)) {
    return { status: "loading", identity, state: null, refresh };
  }

  if (readFailure) {
    return { status: "read-failed", identity: null, state: null, refresh, readFailure };
  }

  if (!identity) {
    return { status: "no-identity", identity: null, state: null, refresh };
  }

  const state =
    (states ?? []).find((s) => s.documentObjectIdentifier === identity.documentObjectIdentifier) ??
    null;

  if (!state) {
    return { status: "stale", identity, state: null, refresh };
  }

  if (state.chatObjectIdentifier) {
    return { status: "chat-scoped", identity, state, refresh };
  }

  if (user?.sub && state.checkedOutByResourceOwnerId !== user.sub) {
    return { status: "held-by-other", identity, state, refresh };
  }

  return { status: "ok", identity, state, refresh };
};

export const verificationMessage = (
  status: VerificationStatus,
  state: DocumentCheckOutState | null
): string => {
  switch (status) {
    case "no-identity":
      return "This document is not checked out from Jylo.";
    case "stale":
      return "This check out is no longer open — the document was checked in, discarded, or force released elsewhere.";
    case "held-by-other":
      return `This document is now checked out by ${state?.checkedOutByName ?? state?.checkedOutByResourceOwnerId ?? "someone else"}.`;
    case "read-failed":
      // The caller has the specific sentence in `readFailure`; this is only the
      // fallback for anywhere that renders a status without it.
      return "Could not read this document's Jylo details just now. Try again.";
    case "chat-scoped":
      return "This document is checked out via the assistant — manual editing is blocked. Check it in from Jylo first.";
    default:
      return "";
  }
};
